import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedSection, StaggerContainer, StaggerItem, GlowButton } from "../../../components/animations/AnimatedElements";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch("https://readdy.ai/api/form/d6tuod9qcrg2vc5apub0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClasses = (fieldName: string) => `
    w-full bg-brand-darkCard border rounded-md px-4 py-2.5 text-sm text-white placeholder-gray-600 
    transition-all duration-300 outline-none
    ${focusedField === fieldName 
      ? 'border-cyan-500/60 shadow-[0_0_15px_rgba(0,229,255,0.15)]' 
      : 'border-cyan-900/30 hover:border-cyan-900/50'
    }
  `;

  const contactInfo = [
    { icon: "ri-building-line", label: "회사명", val: "CITY-AI(씨티에이아이) 주식회사" },
    { icon: "ri-user-star-line", label: "대표이사", val: "정종모" },
    { icon: "ri-mail-line", label: "이메일", val: "jjongmo@cityai.kr" },
    { icon: "ri-phone-line", label: "전화", val: "010-3460-3208" },
  ];

  const cooperationAreas = [
    "공공기관 AI Agent 도입",
    "철도·교통 안전 시스템",
    "디지털트윈 구축",
    "민간기업 보안 솔루션",
    "기술 파트너십"
  ];

  return (
    <section id="contact" className="py-28 bg-brand-darkAlt relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection>
          <div className="section-header">
            <span className="section-accent" />
            <span className="section-label">Contact & Partnership</span>
          </div>
          <h2 className="section-title mb-4">
            연락처 · <span className="text-cyan-400">협력 문의</span>
          </h2>
          <p className="text-gray-400 text-base mb-16 max-w-xl">
            CITY-AI와 함께 AI Agent 기술로 새로운 가치를 만들어 나가세요. 공공기관, 민간기업, 파트너사 모두 문의 환영합니다.
          </p>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Info Panel */}
            <StaggerContainer className="lg:col-span-2 space-y-6" staggerDelay={0.1}>
              <StaggerItem>
                <div className="bg-brand-dark border border-cyan-900/30 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-5">회사 정보</h3>
                  <div className="space-y-4">
                    {contactInfo.map((info, idx) => (
                      <motion.div
                        key={info.label}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <div className="icon-box flex-shrink-0 mt-0.5">
                          <i className={`${info.icon} text-cyan-400 text-sm`} />
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs mb-0.5">{info.label}</div>
                          <div className="text-gray-200 text-sm">{info.val}</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </StaggerItem>

              <StaggerItem>
                <div className="bg-brand-dark border border-cyan-900/30 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-4">주요 협력 분야</h3>
                  <div className="space-y-2">
                    {cooperationAreas.map((item, idx) => (
                      <motion.div
                        key={item}
                        className="flex items-center gap-2 text-sm text-gray-400"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full flex-shrink-0" />
                        {item}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </StaggerItem>
            </StaggerContainer>

            {/* Form */}
            <div className="lg:col-span-3">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="h-full bg-brand-dark border border-cyan-500/40 rounded-lg flex flex-col items-center justify-center p-12 text-center"
                  >
                    <motion.div
                      className="w-16 h-16 flex items-center justify-center bg-cyan-500/10 rounded-full mb-4"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 400, delay: 0.2 }}
                    >
                      <motion.i
                        className="ri-check-line text-cyan-400 text-3xl"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                      />
                    </motion.div>
                    <motion.h3
                      className="text-white text-xl font-bold mb-2"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      문의가 접수되었습니다
                    </motion.h3>
                    <motion.p
                      className="text-gray-400 text-sm"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      빠른 시간 내에 담당자가 연락드리겠습니다.
                    </motion.p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-readdy-form
                    id="contact-form"
                    onSubmit={handleSubmit}
                    className="bg-brand-dark border border-cyan-900/30 rounded-lg p-8 space-y-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-gray-400 text-xs mb-1.5 block">기관·회사명</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('company')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="예) 서울교통공사"
                          className={inputClasses('company')}
                        />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs mb-1.5 block">
                          담당자명 <span className="text-cyan-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="홍길동"
                          required
                          className={inputClasses('name')}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-gray-400 text-xs mb-1.5 block">
                          이메일 <span className="text-cyan-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="example@company.com"
                          required
                          className={inputClasses('email')}
                        />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs mb-1.5 block">연락처</label>
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('phone')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="02-0000-0000"
                          className={inputClasses('phone')}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs mb-1.5 block">관심 서비스</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('service')}
                        onBlur={() => setFocusedField(null)}
                        className={`${inputClasses('service')} cursor-pointer`}
                      >
                        <option value="">선택해주세요</option>
                        <option value="CCTV AI Agent">CCTV 영상 AI Agent & 디지털트윈</option>
                        <option value="지하철 인파사고 예방">지하철 인파사고 예방 시스템</option>
                        <option value="i-DRIVER">i-DRIVER 스마트 블랙박스</option>
                        <option value="경계감시">AI 자동정합 경계감시 솔루션</option>
                        <option value="보안 근태">스마트 보안·근태관리 솔루션</option>
                        <option value="PDU">전력데이터 관리 솔루션(PDU)</option>
                        <option value="기타">기타 / 전체 문의</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs mb-1.5 block">
                        문의 내용 <span className="text-cyan-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="구체적인 도입 목적, 규모, 일정 등을 자유롭게 작성해 주세요. (최대 500자)"
                        required
                        maxLength={500}
                        rows={5}
                        className={`${inputClasses('message')} resize-none`}
                      />
                      <div className="text-right text-gray-600 text-xs mt-1">
                        {formData.message.length}/500
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <GlowButton
                        type="submit"
                        disabled={submitting || !formData.name || !formData.email || !formData.message}
                        className="flex-1"
                      >
                        {submitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <motion.span
                              className="w-4 h-4 border-2 border-brand-dark border-t-transparent rounded-full"
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            />
                            전송 중...
                          </span>
                        ) : (
                          "문의 전송하기"
                        )}
                      </GlowButton>
                      <motion.a
                        href="http://pf.kakao.com/_GLQZX/chat"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-3 bg-[#FEE500] hover:bg-[#FFD700] text-gray-900 font-semibold rounded-md transition-all duration-200 text-sm whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <i className="ri-kakao-talk-fill text-base" />
                        카카오톡으로 문의하기
                      </motion.a>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
